import { useState } from "react";
import { toast } from "sonner";
import { Scale, Check, Image as ImageIcon } from "lucide-react";
import { api, errMsg, useApi } from "../../lib/api";
import { SampleBadge, Decision } from "../Badges";

const STAGES = [["pitch", "Pitch"], ["in_production", "In production"], ["review", "Review"], ["approved", "Approved"], ["published", "Published"]];

const NewStory = ({ onDone }) => {
  const [f, setF] = useState({ title: "", station: "Peace FM", legal_flag: false, radio: true, tv: false, digital: true });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stories", f);
      toast.success("Story pitched");
      setF({ ...f, title: "", legal_flag: false });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const chk = (k, l) => (
    <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`story-${k}-checkbox`} />{l}</label>
  );
  return (
    <form onSubmit={submit} className="panel p-4 flex flex-wrap items-center gap-3 mb-6" data-testid="new-story-form">
      <input required className="field flex-1 min-w-[220px]" placeholder="Story pitch headline" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="story-title-input" />
      <select className="field !w-auto" value={f.station} onChange={(e) => setF({ ...f, station: e.target.value })} data-testid="story-station-select">
        {["Peace FM", "UTV", "Okay FM", "Neat FM", "Hello FM", "Peacefmonline.com"].map((s) => <option key={s}>{s}</option>)}
      </select>
      {chk("radio", "Radio")}{chk("tv", "TV")}{chk("digital", "Web")}{chk("legal_flag", "Sensitive political / legal review")}
      <button className="btn-gold" data-testid="story-create-btn">Pitch</button>
    </form>
  );
};

const StoryCard = ({ s, reload }) => {
  const act = async (fn) => {
    try {
      await fn();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const idx = STAGES.findIndex(([k]) => k === s.stage);
  const nextStage = STAGES[idx + 1]?.[0];
  return (
    <div className="panel p-3" data-testid={`story-card-${s.id}`}>
      <div className="text-sm text-ink">{s.title}</div>
      <div className="text-[11px] text-slate-500 mt-1">{s.station}</div>
      <div className="flex flex-wrap gap-1 mt-2">
        {s.legal_flag && <span className="pill pill-red"><Scale size={10} /> Legal review</span>}
        {s.is_sample && <SampleBadge label="Sample" />}
      </div>
      <div className="mt-2 space-y-1">
        {Object.entries(s.platforms).filter(([, v]) => v.needed).map(([p, v]) => (
          <button key={p} onClick={() => act(() => api.patch(`/stories/${s.id}`, { platform: p, done: !v.done }))} data-testid={`story-platform-${p}-${s.id}`}
            className={`w-full flex items-center gap-2 text-[11px] ${v.done ? "text-green-400" : "text-slate-400"}`}>
            <Check size={11} className={v.done ? "" : "opacity-30"} /> {p === "digital" ? "Web version" : p === "tv" ? "UTV video cut" : "Radio package"}
            {p === "digital" && v.needs_photo && !v.done && <ImageIcon size={11} className="text-amber-400" />}
          </button>
        ))}
      </div>
      <div className="mt-2 border-t border-[color:var(--line)] pt-2 space-y-1">
        {s.approvals.map((a) => (
          <div key={a.step} className="text-[10px] font-data flex justify-between"><span className="text-slate-400">{a.step}</span><span className={a.signed_by ? "text-green-400" : "text-amber-400"}>{a.signed_by || "pending"}</span></div>
        ))}
      </div>
      <div className="flex gap-3 mt-2">
        {s.approvals.some((a) => !a.signed_by) && <button onClick={() => act(() => api.post(`/stories/${s.id}/approve`))} className="text-[11px] text-gold hover:underline" data-testid={`story-signoff-btn-${s.id}`}>Sign off</button>}
        {nextStage && <button onClick={() => act(() => api.patch(`/stories/${s.id}`, { stage: nextStage }))} className="text-[11px] text-slate-300 hover:text-ink" data-testid={`story-advance-btn-${s.id}`}>→ {STAGES[idx + 1][1]}</button>}
      </div>
    </div>
  );
};

export const Newsroom = () => {
  const { data, reload } = useApi("/stories");
  return (
    <div data-testid="newsroom-board">
      <NewStory onDone={reload} />
      <div className="grid md:grid-cols-5 gap-3">
        {STAGES.map(([k, l]) => (
          <div key={k} className="bg-[#F4F8F5] border border-[color:var(--line)] rounded-sm p-3 min-h-[220px]" data-testid={`story-col-${k}`}>
            <div className="eyebrow !text-[10px] mb-3">{l}</div>
            <div className="space-y-2">{(data || []).filter((s) => s.stage === k).map((s) => <StoryCard key={s.id} s={s} reload={reload} />)}</div>
          </div>
        ))}
      </div>
      <Decision>Nothing politically sensitive airs without editor and legal sign-off; each platform's deliverable is tracked separately.</Decision>
    </div>
  );
};
